'use client'
import styles from './scriptblock.module.scss';
import { useEffect } from 'react';
import { logError } from '@/app/helpers/azureBlobLogger';
import { iRequestContext } from '@/app/models/contextModels/requestContext';
import { iScriptBlock } from '@/app/models/blockTypes/scriptBlock';

interface Props {
    block: iScriptBlock;
    requestContext: iRequestContext;
}

function containsScriptTag(js: string) {
    return /<script[\s>]/i.test(js);
}

export default function ScriptBlock({ block, requestContext }: Props) {
    const js = block?.properties?.javaScript || '';
    const placement = block?.properties?.placement;

    useEffect(() => {
        if (!js || placement === 'Inline') return;

        if (containsScriptTag(js)) {
            const container = document.createElement('div');
            container.innerHTML = js;
            const scripts = Array.from(container.querySelectorAll('script'));
            const injectedMap = new Map<HTMLScriptElement, HTMLScriptElement>();

            scripts.forEach(scriptEl => {
                const newScript = document.createElement('script');
                // Use regular for loop for attributes
                for (let i = 0; i < scriptEl.attributes.length; i++) {
                    const attr = scriptEl.attributes[i];
                    newScript.setAttribute(attr.name, attr.value);
                }
                newScript.text = scriptEl.text;
                if (placement === 'Head') {
                    document.head.appendChild(newScript);
                } else {
                    document.body.appendChild(newScript);
                }
                // Track for cleanup
                injectedMap.set(scriptEl, newScript);
            });

            return () => {
                injectedMap.forEach((newScript) => {
                    if (newScript.parentNode) {
                        newScript.parentNode.removeChild(newScript);
                    }
                });
            };
        } else {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.text = js;

            try {
                if (placement === 'Head') {
                    document.head.appendChild(script);
                } else {
                    document.body.appendChild(script);
                }
            } catch (error) {
                logError(
                    "ScriptBlock.tsx",
                    'Error when injecting ScriptBlock',
                    `current url: ${requestContext.url}`,
                    'Error',
                    error
                );
            }

            return () => {
                if (script.parentNode) {
                    script.parentNode.removeChild(script);
                }
            };
        }
    }, [js, placement, requestContext.url]);

    // Inline placement: render directly
    if (placement === 'Inline' && js) {
        if (containsScriptTag(js)) {
            return (
                <div
                    className={`grid gap-8 px-6 xl:px-0 pb-10 lg:pb-12 ${styles.inlineScript}`}
                    dangerouslySetInnerHTML={{ __html: js }}
                />
            );
        } else {
            return (
                <script
                    type="text/javascript"
                    dangerouslySetInnerHTML={{ __html: js }}
                    className={styles.inlineScript}
                />
            );
        }
    }

    return null;
}